// ============================================================
// SheetEffectFields - the fields of the sheet tier's effects (plan
// armoury, stage 3): a characteristic raised or made unnatural, a talent
// or trait granted, wounds, armour, a resistance. What each does is
// `engine/roster/stancedSheet` (the effective sheet); this only edits.
// ============================================================
import type { ReactNode } from 'react';
import { CHARACTERISTICS } from '../../engine/character/characteristicList';
import type { StanceEffect } from '../../engine/character/effectItems';
import { TALENT_CATALOG } from '../../engine/character/talentCatalog';
import { TRAIT_CATALOG } from '../../engine/character/traitCatalog';
import type { CharacteristicId } from '../../engine/character/types';
import common from '../../styles/common.module.css';
import styles from '../../styles/Stances.module.css';
import { Amount, Pick } from './effectFieldParts';

type SheetKind =
  | 'characteristic_bonus'
  | 'unnatural_characteristic'
  | 'grant_talent'
  | 'grant_trait'
  | 'wound_bonus'
  | 'flat_ap_bonus'
  | 'resistance';
type Of<K extends SheetKind> = Extract<StanceEffect, { type: K }>;
type FieldProps<K extends SheetKind> = { effect: Of<K>; set: (next: Of<K>) => void; label: string; locked: boolean };

const CHARACTERISTIC_CHOICES = CHARACTERISTICS.map((c) => ({ id: c.id, label: c.name }));
const byName = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label);
const TALENT_CHOICES = Object.values(TALENT_CATALOG)
  .map((t) => ({ id: t.id, label: t.name }))
  .sort(byName);
const TRAIT_CHOICES = Object.values(TRAIT_CATALOG)
  .map((t) => ({ id: t.id, label: t.name }))
  .sort(byName);

/** A parameter for a granted talent or trait that takes one ("Hatred (Orks)"); none when it takes none. */
function Parameter(props: {
  value: string | undefined;
  takes: boolean;
  onSet: (p: string) => void;
  label: string;
  locked: boolean;
}) {
  if (!props.takes) return null;
  return (
    <input
      className={`${common.input} ${styles.narrow}`}
      aria-label={props.label}
      value={props.value ?? ''}
      maxLength={40}
      disabled={props.locked}
      onChange={(e) => props.onSet(e.target.value)}
    />
  );
}

/** A plain amount: wounds, armour. */
function amountOnly<E extends Of<'wound_bonus'> | Of<'flat_ap_bonus'>>() {
  return function AmountOnly({
    effect,
    set,
    label,
    locked,
  }: {
    effect: E;
    set: (next: E) => void;
    label: string;
    locked: boolean;
  }) {
    return (
      <Amount
        value={effect.value}
        onChange={(value) => set({ ...effect, value })}
        label={`${label}: amount`}
        locked={locked}
      />
    );
  };
}

/** The parameter key spread only when typed: an empty one is no parameter, never an empty string on the wire. */
function withParameter<E extends { parameter?: string }>(effect: E, parameter: string): E {
  const { parameter: _was, ...rest } = effect;
  return (parameter.trim() ? { ...rest, parameter } : rest) as E;
}

export const SHEET_FIELDS: { [K in SheetKind]: (p: FieldProps<K>) => ReactNode } = {
  characteristic_bonus: ({ effect, set, label, locked }) => (
    <>
      <Pick
        value={effect.characteristic}
        options={CHARACTERISTIC_CHOICES}
        onPick={(characteristic: CharacteristicId) => set({ ...effect, characteristic })}
        label={`${label}: characteristic`}
        locked={locked}
      />
      <Amount
        value={effect.value}
        onChange={(value) => set({ ...effect, value })}
        label={`${label}: amount`}
        locked={locked}
      />
    </>
  ),
  unnatural_characteristic: ({ effect, set, label, locked }) => (
    <>
      <Pick
        value={effect.characteristic as CharacteristicId}
        options={CHARACTERISTIC_CHOICES}
        onPick={(characteristic) => set({ ...effect, characteristic })}
        label={`${label}: characteristic`}
        locked={locked}
      />
      <Amount
        value={effect.multiplier}
        onChange={(multiplier) => set({ ...effect, multiplier })}
        label={`${label}: times`}
        locked={locked}
        min={1}
      />
    </>
  ),
  grant_talent: ({ effect, set, label, locked }) => (
    <>
      <Pick
        value={effect.talentId}
        options={TALENT_CHOICES}
        onPick={(talentId) => set({ type: 'grant_talent', talentId })}
        label={`${label}: talent`}
        locked={locked}
      />
      <Parameter
        value={effect.parameter}
        takes={!!TALENT_CATALOG[effect.talentId]?.parameterized}
        onSet={(p) => set(withParameter(effect, p))}
        label={`${label}: of`}
        locked={locked}
      />
    </>
  ),
  grant_trait: ({ effect, set, label, locked }) => (
    <>
      <Pick
        value={effect.traitId}
        options={TRAIT_CHOICES}
        onPick={(traitId) => set({ type: 'grant_trait', traitId })}
        label={`${label}: trait`}
        locked={locked}
      />
      <Parameter
        value={effect.parameter}
        takes={!!TRAIT_CATALOG[effect.traitId]?.parameterized}
        onSet={(p) => set(withParameter(effect, p))}
        label={`${label}: of`}
        locked={locked}
      />
    </>
  ),
  wound_bonus: amountOnly<Of<'wound_bonus'>>(),
  flat_ap_bonus: amountOnly<Of<'flat_ap_bonus'>>(),
  resistance: ({ effect, set, label, locked }) => (
    <>
      <Amount
        value={effect.value}
        onChange={(value) => set({ ...effect, value })}
        label={`${label}: amount`}
        locked={locked}
      />
      <input
        className={`${common.input} ${styles.narrow}`}
        aria-label={`${label}: against`}
        value={effect.against}
        maxLength={40}
        disabled={locked}
        onChange={(e) => set({ ...effect, against: e.target.value })}
      />
    </>
  ),
};
